import React, { useEffect } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScienceBackground from '../../components/layout/ScienceBackground';
import { FaShieldAlt, FaLock, FaUserSecret, FaDatabase, FaCookieBite, FaExclamationCircle } from 'react-icons/fa';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-800 overflow-x-hidden selection:bg-orange-500 selection:text-white relative flex flex-col pt-24">
      <ScienceBackground />
      <Navbar />

      <div className="relative z-10 pt-20 pb-16 px-6 text-center w-full max-w-4xl mx-auto">
        <div className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-600 font-bold text-sm tracking-wide mb-6 border border-blue-200 shadow-sm">
          Privacy Protection 🛡️
        </div>
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight text-slate-800 mb-6 drop-shadow-sm font-sans">
          PRIVACY <span className="text-blue-600">POLICY</span>
        </h1>
        <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed">
          Your privacy is our top priority. Learn how we handle and protect your personal information at Kasun Walakumbura Education Center.
        </p>
      </div>

      <div className="relative z-10 flex-grow px-6 md:px-12 pb-24 max-w-4xl mx-auto w-full">
        <div className="bg-white/80 backdrop-blur-xl border border-white p-8 md:p-12 rounded-[2.5rem] shadow-2xl space-y-12">
          
          <PolicySection 
            icon={<FaShieldAlt className="text-orange-500" />}
            title="Introduction"
            content="We at Kasun Walakumbura Education Center are committed to protecting the privacy of our students and users. This Privacy Policy outlines the types of personal information we collect, how we use it, and the steps we take to ensure your data remains secure."
          />

          <PolicySection 
            icon={<FaUserSecret className="text-blue-500" />}
            title="Information We Collect"
            content={
              <ul className="space-y-4 list-disc list-inside text-slate-600 font-medium ml-4">
                <li>Personal identification information (Name, Phone Number, Address, School).</li>
                <li>Academic records and course enrollment data.</li>
                <li>Payment records and transaction history (processed through secure gateways).</li>
                <li>Technical data such as IP addresses and device information for security purposes.</li>
              </ul>
            }
          />

          <PolicySection 
            icon={<FaDatabase className="text-purple-500" />}
            title="How We Use Your Data"
            content="The information we collect is used primarily to manage your student account, provide access to educational materials, process course enrollments, and communicate important academy updates. We do not sell or rent your personal data to third parties."
          />

          <PolicySection 
            icon={<FaLock className="text-green-500" />}
            title="Data Security"
            content="We implement a variety of security measures to maintain the safety of your personal information. Your data is stored on secure servers, and sensitive information is encrypted. Only authorized staff members have access to student records."
          />

          <PolicySection 
            icon={<FaCookieBite className="text-yellow-600" />}
            title="Cookies & Tracking"
            content="Our website uses cookies to enhance your browsing experience, remember your login preferences, and analyze site traffic. You can choose to disable cookies through your browser settings, though this may affect certain features of the portal."
          />

          <PolicySection 
            icon={<FaExclamationCircle className="text-red-500" />}
            title="Your Rights"
            content="As a user, you have the right to access, update, or request the deletion of your personal information. If you wish to exercise any of these rights or have questions about our privacy practices, please contact our support team."
          />

          <div className="pt-10 border-t border-slate-100">
            <div className="bg-slate-50 p-6 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Last Updated</p>
                <p className="text-sm font-bold text-slate-700">May 10, 2026</p>
              </div>
              <button className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-colors shadow-lg shadow-slate-200">
                Contact Privacy Officer
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const PolicySection = ({ icon, title, content }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-2xl border border-slate-100 shadow-inner">
        {icon}
      </div>
      <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">{title}</h2>
    </div>
    <div className="text-slate-600 font-medium leading-relaxed pl-1">
      {content}
    </div>
  </div>
);

export default PrivacyPolicy;
